import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Heading, Text, Tooltip } from '@livechat/design-system-react-components';

import { useNotifications } from 'contexts/notifications/use-notifications';
import { Logo } from 'components/logo/Logo';
import { HelpCenterFooter } from 'components/help-center/Footer';
import { useAccounts } from 'hooks/use-accounts';
import {
  sortProducts,
  getProductByName,
  getAppLinkByName,
  getProductDisplayName,
} from 'helpers/product';
import { accountsClient } from 'services/connectivity/accounts/client';
import { Account } from 'services/connectivity/accounts/types';
import { organizationsClient } from 'services/connectivity/organizations/client';
import { errorDeserialize } from 'helpers/error-deserializer';

import {
  requestContainerStyles,
  mainContentContainerStyles,
  headerContainerStyles,
  headerTextStyles,
  mainTextStyles,
  logInTextStyles,
  productButtonsContainerStyles,
  productButtonStyles,
} from './styles';

type Props = {
  productName: string;
  accessToken: string;
};

export const RequestAccess: FC<Props> = ({ productName, accessToken }) => {
  const [userProducts, setUserProducts] = useState<string[]>([]);
  const [emailSent, setEmailSent] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  const { accounts } = useAccounts();
  const { showToast } = useNotifications();

  useEffect(() => {
    const getOrganizationData = async () => {
      try {
        const { result: userData, error: meError } = await accountsClient.getMe(accessToken);
        if (meError) {
          throw meError;
        }

        const { result, error } = await organizationsClient.getMy(accessToken);
        if (error) {
          throw error;
        }

        const availableProducts = userData.roles.filter(
          (role) =>
            role.product !== productName &&
            role.organization_id === result.organization_id &&
            role.product !== 'Accounts' &&
            role.role !== 'inviter'
        );
        const productNames = availableProducts.map(({ product }) => product);
        const sortedProducts = sortProducts(productNames);
        setUserProducts(sortedProducts);
        setOrganizationId(result.organization_id);
      } catch (e) {
        throw new Error(`User products couldn't be fetched. [${errorDeserialize(e)}]`);
      }
    };

    getOrganizationData();
  }, []);

  const organizationOwner: Account = useMemo(
    () =>
      accounts?.find((account) =>
        account.roles.find(
          (role) =>
            role.product === 'Accounts' &&
            role.role === 'owner' &&
            role.organization_id === organizationId
        )
      ),
    [accounts, organizationId]
  );

  const handleSendWelcomeMail = async () => {
    setEmailSent(true);
    const { error, response } = await accountsClient.requestForInvitation(
      { product: productName },
      accessToken
    );

    if (error) {
      showToast({
        content:
          response.status === 429
            ? "You've already sent access request"
            : "We couldn't send the email",
        variant: 'error',
      });

      return;
    }

    showToast({
      content: 'Request sent!',
      variant: 'success',
    });
  };

  return (
    <div css={requestContainerStyles}>
      <div css={mainContentContainerStyles}>
        <div css={headerContainerStyles}>
          <Heading size="lg" bold css={headerTextStyles}>
            Do you want to join {getProductDisplayName(productName)}?
          </Heading>
        </div>
        <Text css={mainTextStyles} data-testid="request-text">
          Request access from {getProductDisplayName(productName)} Admin.
        </Text>

        {productName === 'PartnerProgram' ? (
          <Tooltip
            triggerRenderer={() => (
              <Button size="large" kind="primary" disabled>
                Request access
              </Button>
            )}
            kind="invert"
            placement="top"
          >
            <Text data-testid="request-access-pp-tooltip">
              Only the Owner <b>{organizationOwner?.email}</b> can create the Partner profile and
              access the Partner App.
            </Text>
          </Tooltip>
        ) : (
          <Button size="large" kind="primary" disabled={emailSent} onClick={handleSendWelcomeMail}>
            {emailSent ? 'Request sent!' : 'Request access'}
          </Button>
        )}

        <Text css={logInTextStyles}>Log in to the products you already have:</Text>
        <div css={productButtonsContainerStyles} data-testid="active-products">
          {userProducts?.map((product) => (
            <Button
              key={product}
              kind="secondary"
              href={getAppLinkByName(product)}
              target="_blank"
              rel="noopener noreferrer"
              css={productButtonStyles}
            >
              <Logo product={getProductByName(product)} size="small" />
              Go to {getProductDisplayName(product)}
            </Button>
          ))}
        </div>
      </div>
      <HelpCenterFooter />
    </div>
  );
};
